import { CSSProperties, FC, HTMLAttributes, MouseEventHandler, ReactNode } from 'react';
import { MediumPopoverPortal, PopoverAnchor, SmallPopoverPortal } from './Popover.shared';

interface PopoverAnchorViewProps {
  anchorContent: ReactNode;
  className?: string;
  dataTestId: string;
  isOpen: boolean;
  onClick: VoidFunction;
  onMouseEnter: VoidFunction;
  onMouseLeave: VoidFunction;
  anchorRef?: (node: HTMLSpanElement | null) => void;
}

interface PopoverPanelContentProps {
  headerClassName?: string;
  noticeContent: ReactNode;
  noticeTitleProps?: HTMLAttributes<HTMLHeadingElement>;
  title?: string;
  withCloseButton: boolean;
}

interface MediumPopoverViewProps {
  anchor: PopoverAnchorViewProps;
  content: PopoverPanelContentProps;
  isOpen: boolean;
  overlayingNoticeTestId: string;
  portal: boolean;
  onBackdropClick: VoidFunction;
  onClose: VoidFunction;
  onContentClick: MouseEventHandler<HTMLDivElement>;
}

export const MediumPopoverView: FC<MediumPopoverViewProps> = ({
  anchor,
  content,
  isOpen,
  overlayingNoticeTestId,
  portal,
  onBackdropClick,
  onClose,
  onContentClick,
}) => {
  return (
    <>
      <PopoverAnchor
        className={anchor.className}
        dataTestId={anchor.dataTestId}
        isOpen={anchor.isOpen}
        anchorContent={anchor.anchorContent}
        onClick={anchor.onClick}
        onMouseEnter={anchor.onMouseEnter}
        onMouseLeave={anchor.onMouseLeave}
      />
      {isOpen && (
        <MediumPopoverPortal
          dataTestId={anchor.dataTestId}
          overlayingNoticeTestId={overlayingNoticeTestId}
          title={content.title}
          noticeTitleProps={content.noticeTitleProps}
          withCloseButton={content.withCloseButton}
          headerClassName={content.headerClassName}
          noticeContent={content.noticeContent}
          onClose={onClose}
          onContentClick={onContentClick}
          onBackdropClick={onBackdropClick}
          usePortal={portal}
        />
      )}
    </>
  );
};

interface SmallPopoverPanelProps {
  anchorElement: HTMLSpanElement | null;
  dataTestId: string;
  hasPointer: boolean;
  isRaw: boolean;
  layer: 'default' | 'below-header';
  panelAttributes: HTMLAttributes<HTMLDivElement>;
  panelRef: (node: HTMLDivElement | null) => void;
  pointerClassName?: string;
  popperStyle?: CSSProperties;
  portal: boolean;
}

interface SmallPopoverViewProps {
  anchor: PopoverAnchorViewProps;
  content: PopoverPanelContentProps;
  isOpen: boolean;
  panel: SmallPopoverPanelProps;
  onClose: VoidFunction;
  onContentClick: MouseEventHandler<HTMLDivElement>;
}

export const SmallPopoverView: FC<SmallPopoverViewProps> = ({
  anchor,
  content,
  isOpen,
  panel,
  onClose,
  onContentClick,
}) => {
  return (
    <>
      <PopoverAnchor
        anchorRef={anchor.anchorRef}
        className={anchor.className}
        dataTestId={anchor.dataTestId}
        isOpen={anchor.isOpen}
        anchorContent={anchor.anchorContent}
        onClick={anchor.onClick}
        onMouseEnter={anchor.onMouseEnter}
        onMouseLeave={anchor.onMouseLeave}
      />
      {isOpen && panel.anchorElement && (
        <SmallPopoverPortal
          panelRef={panel.panelRef}
          popperStyle={panel.popperStyle}
          panelAttributes={panel.panelAttributes}
          hasPointer={panel.hasPointer}
          isRaw={panel.isRaw}
          layer={panel.layer}
          pointerClassName={panel.pointerClassName}
          dataTestId={panel.dataTestId}
          title={content.title}
          noticeTitleProps={content.noticeTitleProps}
          withCloseButton={content.withCloseButton}
          headerClassName={content.headerClassName}
          noticeContent={content.noticeContent}
          onClose={onClose}
          onContentClick={onContentClick}
          onMouseEnter={anchor.onMouseEnter}
          onMouseLeave={anchor.onMouseLeave}
          usePortal={panel.portal}
        />
      )}
    </>
  );
};
